import React, { useState } from 'react';
import { User } from '../../../../../shared/types';
import userProfileService from '../../../../../shared/services/userProfileService';
import { useDispatch } from 'react-redux';
import { updateUser } from '../../../../../core/store/slices/userSlice';
import usePageTranslation from '../../../../../shared/hooks/usePageTranslation';

interface AccountSettingsTabProps {
  currentUser: User;
  onLogout: () => void;
}

const AccountSettingsTab: React.FC<AccountSettingsTabProps> = ({ currentUser, onLogout }) => {
  const { t } = usePageTranslation('game', 'SettingsModal');
  const dispatch = useDispatch();
  
  // États pour le changement de mot de passe
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [passwordSuccess, setPasswordSuccess] = useState<string | null>(null);
  const [changingPassword, setChangingPassword] = useState(false);
  
  // États pour le changement d'email
  const [showChangeEmail, setShowChangeEmail] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [emailPassword, setEmailPassword] = useState('');
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [changingEmail, setChangingEmail] = useState(false);

  const handleChangePassword = async () => {
    setPasswordError(null);
    setPasswordSuccess(null);

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError(t('account.security.fieldsRequired'));
      return;
    }

    // Validation améliorée du mot de passe
    if (newPassword.length < 8) {
      setPasswordError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError(t('account.security.passwordsMismatch'));
      return;
    }

    try {
      setChangingPassword(true);
      const response = await userProfileService.changePassword(currentPassword, newPassword);
      
      if (response.success) {
        setPasswordSuccess(t('account.security.passwordChanged'));
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
        setTimeout(() => {
          setShowChangePassword(false);
          setPasswordSuccess(null);
        }, 2000);
      }
    } catch (err: any) {
      setPasswordError(err.message || t('account.security.passwordError'));
    } finally {
      setChangingPassword(false);
    }
  };

  const handleChangeEmail = async () => {
    setEmailError(null);
    setEmailSuccess(null);

    if (!newEmail || !emailPassword) {
      setEmailError(t('account.security.fieldsRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      setEmailError(t('account.emailSection.invalidEmailFormat'));
      return;
    }

    if (newEmail === currentUser.email) {
      setEmailError(t('account.emailSection.emailMustBeDifferent'));
      return;
    }

    try {
      setChangingEmail(true);
      const response = await userProfileService.changeEmail(newEmail, emailPassword);
      
      if (response.success && response.data) {
        setEmailSuccess(t('account.emailSection.emailChanged'));
        dispatch(updateUser({ email: response.data.email }));
        setNewEmail('');
        setEmailPassword('');
        setTimeout(() => {
          setShowChangeEmail(false);
          setEmailSuccess(null);
        }, 2000);
      }
    } catch (err: any) {
      setEmailError(err.message || t('account.emailSection.emailError'));
    } finally {
      setChangingEmail(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">{t('account.title')}</h3>
        <div className="bg-gray-800/50 rounded-lg p-6">
          <div className="grid md:grid-cols-2 gap-4 text-gray-300">
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('account.username')}</p>
              <p className="text-lg font-semibold">{currentUser.username}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('account.email')}</p>
              <p className="text-lg font-semibold">{currentUser.email}</p>
            </div>
            <div>
              <p className="text-sm text-gray-400 mb-1">{t('account.memberSince')}</p>
              <p className="text-lg font-semibold">
                {new Date(currentUser.created_at).toLocaleDateString('fr-FR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {currentUser.premium && (
        <div className="bg-gradient-to-r from-yellow-900/50 to-orange-900/50 border border-yellow-500/50 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-white mb-2 flex items-center">
            <span className="mr-2">⭐</span>
            {t('account.premium.title')}
          </h3>
          <p className="text-gray-300">
            {t('account.premium.description')}
          </p>
        </div>
      )}

      {/* Changement de mot de passe */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">{t('account.security.title')}</h3>
        <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
          {!showChangePassword ? (
            <button
              onClick={() => setShowChangePassword(true)}
              className="w-full px-4 py-3 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
              <span>{t('account.security.changePassword')}</span>
            </button>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 font-medium mb-2">{t('account.security.currentPassword')}</label>
                <input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t('account.security.currentPasswordPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-gray-300 font-medium mb-2">{t('account.security.newPassword')}</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t('account.security.newPasswordPlaceholder')}
                />
              </div>
              <div>
                <label className="block text-gray-300 font-medium mb-2">{t('account.security.confirmPassword')}</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t('account.security.confirmPasswordPlaceholder')}
                />
              </div>
              {passwordError && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {passwordError}
                </div>
              )}
              {passwordSuccess && (
                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
                  {passwordSuccess}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleChangePassword}
                  disabled={changingPassword}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingPassword ? t('account.security.changing') : t('account.security.confirm')}
                </button>
                <button
                  onClick={() => {
                    setShowChangePassword(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                    setPasswordError(null);
                    setPasswordSuccess(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  {t('account.security.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Changement d'email */}
      <div>
        <h3 className="text-xl font-semibold text-white mb-4">{t('account.emailSection.title')}</h3>
        <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
          {!showChangeEmail ? (
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-300 font-medium">{t('account.emailSection.currentEmail')}</p>
                <p className="text-sm text-gray-400 mt-1">{currentUser.email}</p>
              </div>
              <button
                onClick={() => setShowChangeEmail(true)}
                className="px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                <span>{t('account.emailSection.changeEmail')}</span>
              </button>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="block text-gray-300 font-medium mb-2">{t('account.emailSection.newEmail')}</label>
                <input
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="nouveau@email.com"
                />
              </div>
              <div>
                <label className="block text-gray-300 font-medium mb-2">{t('account.emailSection.confirmPassword')}</label>
                <input
                  type="password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder={t('account.emailSection.passwordPlaceholder')}
                />
              </div>
              {emailError && (
                <div className="p-3 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 text-sm">
                  {emailError}
                </div>
              )}
              {emailSuccess && (
                <div className="p-3 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 text-sm">
                  {emailSuccess}
                </div>
              )}
              <div className="flex gap-3">
                <button
                  onClick={handleChangeEmail}
                  disabled={changingEmail}
                  className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {changingEmail ? t('account.security.changing') : t('account.security.confirm')}
                </button>
                <button
                  onClick={() => {
                    setShowChangeEmail(false);
                    setNewEmail('');
                    setEmailPassword('');
                    setEmailError(null);
                    setEmailSuccess(null);
                  }}
                  className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
                >
                  {t('account.security.cancel')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-gray-700 pt-6">
        <button
          onClick={onLogout}
          className="w-full px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors flex items-center justify-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
          </svg>
          <span>{t('actions.logout')}</span>
        </button>
      </div>
    </div>
  );
};

export default AccountSettingsTab;

